import { styled } from "@mui/material/styles";
import { Box, Button, Stack, Typography } from "@mui/material";
import { colors, shadows } from "../../../theme/theme";

export const StyledCardWrapper = styled(Box)({
  width: "100%",
  height: "100%",
  minHeight: 0,
  display: "flex",
});

export const StyledCard = styled(Box, {
  shouldForwardProp: (prop) => !["clickable", "cardHeight"].includes(prop),
})(({ theme, clickable, cardHeight }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  minHeight: cardHeight || "200px",
  height: "auto",
  flex: 1,
  marginBottom: 0,
  cursor: clickable ? "pointer" : "default",
  backgroundColor: "#fff",
  borderRadius: "30px",
  boxShadow: shadows.card,
  padding: theme.spacing(3),
  paddingTop: theme.spacing(2),
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  maxWidth: "1200px",
  width: "100%",
  overflow: "hidden",
  ...(clickable && {
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: shadows.cardHover,
    },
  }),
  [theme.breakpoints.down("sm")]: {
    minHeight: "unset",
    marginBottom: theme.spacing(2),
  },
}));

export const StyledHeader = styled(Stack, {
  shouldForwardProp: (prop) =>
    !["headerClickable", "cardClickable"].includes(prop),
})(({ theme, headerClickable, cardClickable }) => ({
  flexDirection: "row",
  justifyContent: "space-between",
  borderBottom: `1px solid ${colors.lightBorder}`,
  paddingBottom: theme.spacing(1),
  cursor: headerClickable || cardClickable ? "pointer" : "default",
  gap: theme.spacing(2),
  minWidth: 0,
  flexShrink: 0,
  minHeight: "4.5rem",
  alignItems: "center",
  "& .MuiSvgIcon-root": {
    fontSize: "24px",
    color: theme.palette.text.primary,
    flexShrink: 0,
    marginTop: "2px",
  },
}));

export const StyledTitle = styled(Typography)(({ theme }) => ({
  ...theme.heading2,
  overflow: "hidden",
  textOverflow: "ellipsis",
  wordBreak: "break-word",
  minWidth: 0,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  [theme.breakpoints.down("md")]: {
    fontSize: "22px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "18px",
  },
}));

export const StyledAltTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Quicksand', sans-serif",
  fontWeight: 600,
  fontSize: "22px",
  color: theme.palette.text.primary,
  overflow: "hidden",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  wordBreak: "break-word",
  minWidth: 0,
  [theme.breakpoints.down("md")]: {
    fontSize: "20px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "18px",
  },
}));

export const StyledBodyText = styled(Typography)(({ theme }) => ({
  ...theme.body,
  lineHeight: "24px",
  width: "100%",
  overflowWrap: "break-word",
  wordBreak: "break-word",
  minWidth: 0,
  [theme.breakpoints.down("md")]: {
    fontSize: theme.spacing(2),
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
  },
}));

export const StyledBodyText2 = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "hasList",
})(({ theme, hasList }) => ({
  ...theme.body,
  lineHeight: "24px",
  marginTop: hasList ? theme.spacing(-4) : theme.spacing(-1),
  marginBottom: hasList ? theme.spacing(-4) : theme.spacing(-1),
  marginLeft: hasList ? theme.spacing(-2) : 0,
  width: "100%",
  overflowWrap: "break-word",
  wordBreak: "break-word",
  minWidth: 0,
  [theme.breakpoints.down("md")]: {
    fontSize: theme.spacing(2),
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
  },
}));

export const StyledImage = styled("img")(({ theme }) => ({
  width: "100%",
  maxWidth: "100%",
  height: "auto",
  borderRadius: theme.spacing(2),
  marginBottom: "10px",
  objectFit: "contain",
  marginTop: "auto",
}));

export const ExpandableTextWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isExpanded",
})(({ theme, isExpanded }) => ({
  maxHeight: isExpanded ? "none" : "75px",
  paddingTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
  overflow: isExpanded ? "visible" : "hidden",
  transition: "max-height 0.3s ease-in-out",
}));

export const ReadMoreButton = styled(Button)(({ theme }) => ({
  cursor: "pointer",
  backgroundColor: "transparent",
  border: `1px solid ${colors.secondary}`,
  borderRadius: "10px",
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  marginTop: theme.spacing(1.5),
  display: "inline-block",
  transition: "all 0.2s ease",
  width: "fit-content",
  "&:hover": {
    backgroundColor: "rgba(41, 64, 90, 0.04)",
  },
}));

export const ReadMoreButtonText = styled(Typography)({
  color: colors.secondary,
  fontSize: "14px",
  fontFamily: "'Quicksand', sans-serif",
  fontWeight: 600,
});
